import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import {
  createRecurringHold,
  listVenuesBasic,
  getAllUsersBasic,
  searchUsersClient,
} from "./reservationsApi";

import { listVenueGrounds as listVenueGroundsByVenue } from "../Venues/venueGroundsApi";

import { ArrowLeft, Search, UserRound, Phone } from "lucide-react";

import FormButton from "../../../components/form/FormButton";
import FormField from "../../../components/form/FormField";
import FormInput from "../../../components/form/FormInput";
import FormSelect from "../../../components/form/FormSelect";

import "./recurringHold.css";

const dayOptions = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const ccRe = /^\+\d{1,4}$/;

const onlyDigits = (s) => String(s || "").replace(/\D/g, "");

const buildE164 = (cc, mobile) => {
  const c = String(cc || "").trim();
  const m = onlyDigits(mobile);

  if (!ccRe.test(c) || m.length < 6 || m.length > 14) return "";

  return `${c}${m}`;
};

const RecurringHoldForm = () => {
  const nav = useNavigate();
  const { pathname } = useLocation();

  const basePath = pathname.startsWith("/venue-admin")
    ? "/venue-admin"
    : pathname.startsWith("/country-admin")
    ? "/country-admin"
    : "/super-admin";

  const isVenueAdmin = pathname.startsWith("/venue-admin");

  const [params] = useSearchParams();

  const qUserId = params.get("userId") || "";
  const qUserLabel = params.get("user") || "";

  const [users, setUsers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [grounds, setGrounds] = useState([]);

  const [userId, setUserId] = useState(qUserId);
  const [userChoices, setUserChoices] = useState([]);

  const [vaName, setVaName] = useState("");
  const [vaEmail, setVaEmail] = useState("");
  const [vaCountryCode, setVaCountryCode] = useState("+977");
  const [vaMobile, setVaMobile] = useState("");

  const [qUser, setQUser] = useState("");
  const [showDrop, setShowDrop] = useState(false);

  const [venueId, setVenueId] = useState("");
  const [groundId, setGroundId] = useState("");

  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [weeks, setWeeks] = useState(4);
  const [cutoffDays, setCutoffDays] = useState(3);
  const [totalAmount, setTotalAmount] = useState("");

  const [timeSlots, setTimeSlots] = useState([{ start: "19:00", end: "20:00" }]);

  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (isVenueAdmin) {
      setUserId("");
      setQUser("");
      setUserChoices([]);
      setShowDrop(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVenueAdmin]);

  useEffect(() => {
    (async () => {
      try {
        if (!isVenueAdmin) {
          const u = await getAllUsersBasic();
          setUsers(u || []);
        }
      } catch {
        // noop
      }

      try {
        const v = await listVenuesBasic({ pageSize: 200 });
        setVenues(v || []);
      } catch {
        toast.error("Failed to load venues");
      }
    })();
  }, [isVenueAdmin]);

  useEffect(() => {
    if (isVenueAdmin) return;

    if (qUserLabel) {
      setQUser(qUserLabel);
      return;
    }

    if (qUserId && users.length) {
      const hit = users.find((u) => String(u.id) === String(qUserId));

      if (hit) {
        const label = `${hit.name || "—"}${hit.phone ? " • " + hit.phone : ""}${
          hit.email ? " • " + hit.email : ""
        }`;

        setQUser(label);
      }
    }
  }, [qUserId, qUserLabel, users, isVenueAdmin]);

  useEffect(() => {
    if (isVenueAdmin) return;

    let alive = true;

    const run = async () => {
      const q = (qUser || "").trim();

      if (!q) {
        if (alive) setUserChoices([]);
        return;
      }

      try {
        const items = await searchUsersClient(q);
        if (alive) setUserChoices(items || []);
      } catch {
        if (alive) setUserChoices([]);
      }
    };

    const t = setTimeout(run, 200);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [qUser, isVenueAdmin]);

  useEffect(() => {
    (async () => {
      setGrounds([]);
      setGroundId("");

      if (!venueId) return;

      try {
        const g = await listVenueGroundsByVenue(venueId, { pageSize: 200 });
        setGrounds(Array.isArray(g) ? g : g?.items || []);
      } catch {
        toast.error("Failed to load grounds");
      }
    })();
  }, [venueId]);

  const setSlot = (i, key, val) => {
    setTimeSlots((prev) =>
      prev.map((slot, index) => (index === i ? { ...slot, [key]: val } : slot)),
    );
  };

  const vaE164 = buildE164(vaCountryCode, vaMobile);

  const canSubmit = Boolean(
    (isVenueAdmin ? vaName.trim() && vaE164 : userId) &&
      venueId &&
      groundId &&
      timeSlots.length > 0 &&
      Number(totalAmount) > 0,
  );

  const onSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) {
      toast.info("Fill all required fields");
      return;
    }

    setSubmitting(true);
    setResults(null);

    try {
      const perReservationAmount = Number(totalAmount);

      const timeSlotsPayload =
        Array.isArray(timeSlots) && timeSlots.length === 1
          ? timeSlots.map((slot) => ({
              start: slot.start,
              end: slot.end,
              slotPrice: perReservationAmount,
            }))
          : timeSlots.map((slot) => ({
              start: slot.start,
              end: slot.end,
            }));

      const basePayload = {
        venue: venueId,
        venueGround: groundId,
        dayOfWeek: Number(dayOfWeek),
        timeSlots: timeSlotsPayload,
        weeks: Number(weeks),
        cutoffDays: Number(cutoffDays),
        totalAmount: perReservationAmount,
      };

      const e164 = vaE164;

      if (isVenueAdmin && vaEmail.trim()) {
        const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(vaEmail.trim());

        if (!ok) {
          toast.error("Enter a valid email or keep it blank");
          setSubmitting(false);
          return;
        }
      }

      const payload = isVenueAdmin
        ? {
            ...basePayload,
            userName: vaName.trim(),
            email: vaEmail.trim() ? vaEmail.trim().toLowerCase() : undefined,
            contactNumber: e164,
            countryCode: vaCountryCode.trim(),
            mobileNumber: onlyDigits(vaMobile),
          }
        : {
            ...basePayload,
            userId,
          };

      if (isVenueAdmin && !e164) {
        toast.error("Enter valid country code and mobile number");
        setSubmitting(false);
        return;
      }

      const raw = await createRecurringHold(payload);

      const nextResults = raw?.results || raw?.reservation?.results || [];
      setResults(nextResults);

      const createdCount = nextResults.filter((r) => r?.status === "created").length;
      const deadlineSkippedCount = nextResults.filter(
        (r) => r?.status === "skipped_deadline",
      ).length;
      const conflictSkippedCount = nextResults.filter(
        (r) => r?.status === "skipped_conflict",
      ).length;

      if (deadlineSkippedCount > 0 || conflictSkippedCount > 0) {
        const parts = [];

        if (createdCount > 0) {
          parts.push(
            `${createdCount} eligible hold${createdCount === 1 ? "" : "s"} created.`,
          );
        } else {
          parts.push("No holds were created.");
        }

        if (deadlineSkippedCount > 0) {
          parts.push(
            `${deadlineSkippedCount} occurrence${
              deadlineSkippedCount === 1 ? " was" : "s were"
            } skipped because the payment cutoff has already passed.`,
          );
        }

        if (conflictSkippedCount > 0) {
          parts.push(
            `${conflictSkippedCount} occurrence${
              conflictSkippedCount === 1 ? " was" : "s were"
            } skipped because the slot is unavailable.`,
          );
        }

        if (createdCount === 0 && deadlineSkippedCount > 0) {
          parts.push("Reduce the cutoff days or choose a later occurrence.");
        }

        toast.warning(parts.join(" "), { autoClose: 6500 });
      } else if (createdCount > 0) {
        toast.success(
          `${createdCount} recurring hold${createdCount === 1 ? "" : "s"} created.`,
        );
      } else {
        toast.info("No recurring holds were created.");
      }
    } catch (e2) {
      toast.error(e2?.response?.data?.message || "Failed to create holds");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rh-page">
      <div className="rh-container">
        <div className="rh-top">
          <FormButton
            type="button"
            variant="secondary"
            className="rh-back"
            onClick={() => nav(-1)}
          >
            <ArrowLeft size={18} /> Back
          </FormButton>

          <div className="rh-title">
            <h1>Create Recurring Hold</h1>

            <div className="rh-sub">
              Reserve weekly slots for a user without immediate payment.
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} noValidate>
          <div className="rh-card">
            <div className="rh-row">
              <div className="rh-user-field">
                <FormField label={isVenueAdmin ? "Player" : "User"}>
                  {isVenueAdmin ? (
                    <FormInput
                      placeholder="Enter player name"
                      value={vaName}
                      onChange={(event) => setVaName(event.target.value)}
                    />
                  ) : (
                    <div
                      className="rh-input-ic-wrap"
                      onFocus={() => setShowDrop(true)}
                    >
                      <Search className="rh-ic" size={16} />

                      <FormInput
                        placeholder="Search user by name or phone"
                        value={qUser}
                        onChange={(event) => {
                          setQUser(event.target.value);
                          setShowDrop(true);
                          setUserId("");
                        }}
                        onBlur={() => setTimeout(() => setShowDrop(false), 150)}
                        className="rh-input-with-icon"
                      />
                    </div>
                  )}

                  <div className="rh-muted">
                    {isVenueAdmin ? (
                      <>Email & mobile will be entered below</>
                    ) : userId ? (
                      <>
                        Selected user ID: <strong>{userId}</strong>
                      </>
                    ) : (
                      <>Selected user ID will appear here</>
                    )}
                  </div>
                </FormField>

                {!isVenueAdmin && showDrop && userChoices.length > 0 && !userId && (
                  <div className="rh-dd">
                    {userChoices.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        className="rh-dd-item"
                        onMouseDown={() => {
                          setUserId(u.id);

                          const label = `${u.name || "—"}${
                            u.phone ? " • " + u.phone : ""
                          }${u.email ? " • " + u.email : ""}`;

                          setQUser(label);
                          setShowDrop(false);
                        }}
                        title={u.email}
                      >
                        <UserRound size={16} className="rh-dd-icon" />

                        <span>
                          <span className="rh-dd-title">{u.name || "—"}</span>

                          <span className="rh-dd-sub">
                            {[u.phone, u.email].filter(Boolean).join(" • ")}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <FormField label="Venue">
                <FormSelect
                  value={venueId}
                  onChange={(event) => setVenueId(event.target.value)}
                >
                  <option value="">— Select venue —</option>

                  {venues.map((v) => (
                    <option key={v._id || v.id} value={v._id || v.id}>
                      {v.name}
                    </option>
                  ))}
                </FormSelect>
              </FormField>

              <FormField label="Ground">
                <FormSelect
                  value={groundId}
                  onChange={(event) => setGroundId(event.target.value)}
                  disabled={!venueId || grounds.length === 0}
                >
                  <option value="">— Select ground —</option>

                  {grounds.map((g) => (
                    <option key={g._id || g.id} value={g._id || g.id}>
                      {g.name}
                    </option>
                  ))}
                </FormSelect>
              </FormField>

              <FormField label="Day of Week">
                <FormSelect
                  value={dayOfWeek}
                  onChange={(event) => setDayOfWeek(event.target.value)}
                >
                  {dayOptions.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </FormSelect>
              </FormField>
            </div>

            <div className="rh-row">
              <FormField label="Number of Weeks">
                <FormInput
                  type="number"
                  min={1}
                  max={16}
                  value={weeks}
                  onChange={(event) => setWeeks(event.target.value)}
                />
              </FormField>

              <FormField label="Payment Cutoff (days before)">
                <FormInput
                  type="number"
                  min={1}
                  max={7}
                  value={cutoffDays}
                  onChange={(event) => setCutoffDays(event.target.value)}
                />
                <div className="rh-muted">
                  Occurrences whose payment cutoff has already passed will not be created.
                </div>
              </FormField>

              <FormField label="Amount per Reservation">
                <FormInput
                  type="number"
                  min={0}
                  value={totalAmount}
                  onChange={(event) => setTotalAmount(event.target.value)}
                  placeholder="e.g. 1200"
                />

                {!!totalAmount && <div className="rh-muted">{fmtINR(totalAmount)}</div>}
              </FormField>

              {isVenueAdmin ? (
                <FormField label="Email (optional)">
                  <FormInput
                    placeholder="user@email.com"
                    value={vaEmail}
                    onChange={(event) => setVaEmail(event.target.value)}
                  />
                </FormField>
              ) : (
                <div className="rh-field-spacer" />
              )}
            </div>

            {isVenueAdmin && (
              <div className="rh-row rh-row-bottom">
                <FormField label="Mobile (Country Code)">
                  <FormSelect
                    value={vaCountryCode}
                    onChange={(event) => setVaCountryCode(event.target.value)}
                  >
                    <option value="+977">+977 (Nepal)</option>
                    <option value="+91">+91 (India)</option>
                    <option value="+966">+966 (Saudi)</option>
                    <option value="+971">+971 (UAE)</option>
                  </FormSelect>
                </FormField>

                <FormField label="Mobile Number" className="rh-span-2">
                  <div className="rh-input-ic-wrap">
                    <Phone className="rh-ic" size={16} />

                    <FormInput
                      placeholder="9876543210"
                      value={vaMobile}
                      onChange={(event) => setVaMobile(event.target.value)}
                      inputMode="numeric"
                      className="rh-input-with-icon"
                    />
                  </div>
                </FormField>

                <div className="rh-field-spacer" />
              </div>
            )}
          </div>

          <div className="rh-card">
            <h3 className="rh-slots-title">Weekly Time Slots</h3>

            <div className="rh-muted">
              Each slot will be reserved every selected week.
            </div>

            {timeSlots.map((slot, index) => (
              <div key={index} className="rh-slot">
                <FormField label="Start Time">
                  <FormInput
                    type="time"
                    value={slot.start}
                    onChange={(event) => setSlot(index, "start", event.target.value)}
                  />
                </FormField>

                <FormField label="End Time">
                  <FormInput
                    type="time"
                    value={slot.end}
                    onChange={(event) => setSlot(index, "end", event.target.value)}
                  />
                </FormField>
              </div>
            ))}
          </div>

          <div className="rh-footer">
            <FormButton type="submit" variant="primary" disabled={!canSubmit || submitting}>
              {submitting ? "Creating…" : "Create Holds"}
            </FormButton>

            <FormButton
              type="button"
              variant="secondary"
              onClick={() => nav(`${basePath}/reservations/upcoming`)}
            >
              Go to Upcoming
            </FormButton>
          </div>
        </form>

        {Array.isArray(results) && (
          <div className="rh-results">
            <h3 className="rh-slots-title">Result</h3>

            <div className="rh-muted">Created/processed holds in this request.</div>

            {results.length === 0 ? (
              <div className="rh-muted rh-empty-result">No entries created.</div>
            ) : (
              <div className="rh-table-wrap">
                <table className="rh-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Payment Deadline</th>
                      <th>Reservation Id</th>
                    </tr>
                  </thead>

                  <tbody>
                    {results.map((r, index) => (
                      <tr key={`${r.reservationId || r.date || "result"}-${index}`}>
                        <td>{index + 1}</td>
                        <td>{r.date}</td>
                        <td>
                          <span className="rh-badge">
                            {({
                              created: "Created",
                              skipped_deadline: "Cutoff passed",
                              skipped_conflict: "Conflict",
                            }[r.status] || r.status)}
                          </span>
                        </td>
                        <td>
                          {r.paymentDeadline
                            ? new Date(r.paymentDeadline).toLocaleString()
                            : "—"}
                        </td>
                        <td>{r.reservationId || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecurringHoldForm;